import axios from "axios";
import { useMutation } from "@tanstack/vue-query";
import { useRouter } from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import { useToast } from "primevue";
import { computed, MaybeRefOrGetter, toValue } from "vue";
import { useUser } from "./user";

type LoginCredentials = {
    email: string;
    password: string;
    remember: boolean;
};

type RegisterCredentials = {
    email: string;
    password: string;
    name: string;
    token: string;
};

type ResetCredentials = {
    email: string;
    password: string;
    token: string;
};

type EmailCredentials = {
    email: string;
};

const ax = axios.create();

export async function useAuthStatus(): Promise<boolean> {
    const authenticated = sessionStorage.getItem("authenticated");
    const authenticatedExpiryTime = sessionStorage.getItem(
        "authenticatedExpiryTime",
    );
    if (
        authenticated == null ||
        authenticatedExpiryTime == null ||
        new Date().getTime() > Number(authenticatedExpiryTime)
    ) {
        try {
            const response = await axios.get("/auth-status");
            const status = response.data.authenticated;
            sessionStorage.setItem("authenticated", String(status));
            if (status) {
                sessionStorage.setItem(
                    "authenticatedExpiryTime",
                    String(new Date().getTime() + 1000 * 60 * 15),
                ); // Set expiry time to 15 minutes
            } else {
                sessionStorage.removeItem("authenticatedExpiryTime");
            }
            return status;
        } catch (error) {
            console.error("Error fetching auth status:", error);
            clearAuthStatus();
            return false;
        }
    }
    return authenticated == "true";
}

export function clearAuthStatus() {
    sessionStorage.removeItem("authenticated");
    sessionStorage.removeItem("authenticatedExpiryTime");
}

export function useLogin() {
    const router = useRouter();
    return {
        ...useMutation({
            mutationFn: async function (auth: LoginCredentials) {
                clearAuthStatus();
                await ax.get("/sanctum/csrf-cookie");
                const reponse = await ax.post("/login", auth);
                await useAuthStatus();
                return reponse;
            },
            onSuccess: function () {
                router.push({ name: "home" });
            },
        }),
        default: {
            email: "",
            password: "",
            remember: false,
        },
    };
}

export function useLogout() {
    const router = useRouter();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async function () {
            router.push({ name: "login" });
            setTimeout(function () {
                queryClient.setQueriesData({ queryKey: [] }, null);
                queryClient.clear();
                clearAuthStatus();
            }, 0);
            return await ax.post("/logout", {});
        },
        onSettled: function () {
            console.clear();
        },
    });
}

export function useRegister() {
    const router = useRouter();
    return useMutation({
        mutationFn: async function (auth: RegisterCredentials) {
            return await axios.post("/set-password", auth);
        },
        onSuccess: async function () {
            await useAuthStatus();
            router.push({ name: "home" });
        },
    });
}

export function useResetPassword() {
    const router = useRouter();
    return useMutation({
        mutationFn: async function (auth: ResetCredentials) {
            await ax.get("/sanctum/csrf-cookie");
            return await ax.post("/reset-password", auth);
        },
        onSuccess: async function () {
            await useAuthStatus();
            router.push({ name: "home" });
        },
    });
}

export function useForgotPassword(redirect: boolean = true) {
    const router = useRouter();
    const toast = useToast();
    return {
        ...useMutation({
            mutationFn: async function (data: EmailCredentials) {
                await ax.get("/sanctum/csrf-cookie");
                return await ax.post("/forgot-password", data);
            },
            onSuccess: function () {
                if (redirect) router.push({ name: "login" });
                toast.add({
                    severity: "success",
                    summary: "Success",
                    detail: "Password reset link sent to your email.",
                    life: 3000,
                });
            },
            onError: function (error: any) {
                toast.add({
                    severity: "error",
                    summary: "Error",
                    detail:
                        error.response?.data?.message ||
                        "Failed to send forgotten password link.",
                    life: 3000,
                });
            },
        }),
        default: {
            email: "",
        },
    };
}

export function can(permission: string, permissionable: MaybeRefOrGetter<any>) {
    return toValue(permissionable)?.current_user_permissions[permission];
}

export function cannot(
    permission: string,
    permissionable: MaybeRefOrGetter<any>,
) {
    return !can(permission, permissionable);
}

export function useResendInvite() {
    const toast = useToast();
    return useMutation({
        mutationFn: async function (data: EmailCredentials) {
            return await ax.post("/resend-invite", data);
        },
        onSuccess: async function () {
            toast.add({
                severity: "success",
                summary: "Success",
                detail: "Invite resent successfully.",
                life: 3000,
            });
        },
        onError: function (error: any) {
            toast.add({
                severity: "error",
                summary: "Error",
                detail:
                    error.response?.data?.message || "Failed to resend invite.",
                life: 3000,
            });
        },
    });
}

export function useLoginAs() {
    const router = useRouter();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async function (userId: number) {
            return await ax.post(`/user/${userId}/login-as`, {});
        },
        onSuccess: async function () {
            queryClient.clear();
            await ax.get("/sanctum/csrf-cookie");
            clearAuthStatus();
            await useAuthStatus();
            router.push({ name: "home" });
        },
        onError: function () {
            queryClient.clear();
            clearAuthStatus();
            console.log("Login as failed, cleared query cache and auth status");
            router.push({ name: "login" });
        },
    });
}

export function useIsSuperAdmin() {
    const { data: user, isPending } = useUser();
    return computed<boolean>(() => !isPending.value && user.value.is_admin);
}
